import Button from 'components/Button';
import FormGroup from 'components/FormGroup';
import Modal from 'components/Modal';
import SelectField from 'components/SelectField';
import Typography from 'components/Typography';

import {ModifiedRoute, Route} from 'pages/DeliveryPlanner/useDeliveryPlanner';
import React, {useCallback, useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import styles from './SendRouteModal.module.scss';
import TextField from 'components/TextField';
import {Driver, Vehicle} from 'types';
import {SingleValue} from 'react-select';

export type SendRouteDto = {
  driver: SingleValue<{value: number; label: string}>;
  vehicle: SingleValue<{value: number; label: string}>;
  route_name: string;
};

type SendRouteModalProps = {
  isOpen: boolean;
  team?: Vehicle;
  route: Route;
  onClose: () => void;
  onSubmit: (route: ModifiedRoute) => Promise<any>;
  isIndividual: boolean;
  selectedWorker?: Driver | Vehicle;
  teams: Vehicle[];
  workers: Driver[];
  onChangeWorker: (worker: Driver | Vehicle) => void;
};

const SendRouteModal: React.VFC<SendRouteModalProps> = ({
  isOpen,
  onChangeWorker,
  onClose,
  onSubmit,
  route,
  team,
  selectedWorker,
  isIndividual,
  teams,
  workers,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: {isSubmitting},
  } = useForm<SendRouteDto>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      driver: undefined,
      vehicle: undefined,
    },
  });
  // const {vehicles = []} = useVehicles();

  const handleSubmitForm = useCallback(
    async (data: SendRouteDto) => {
      const vehicle = teams.find(
        (vehicle) => data.vehicle?.value === vehicle.id
      );

      await onSubmit({
        ...route,
        route_name: data.route_name,
        team: isIndividual ? 0 : Number(vehicle?.id || team?.id),
        driver: isIndividual ? (selectedWorker as Driver)?.driver_id! : 0,
      });

      onClose();
    },
    [onClose, onSubmit, team, route, teams, isIndividual, selectedWorker]
  );

  useEffect(() => {
    reset({
      driver: selectedWorker
        ? {
            value: (selectedWorker as Driver)?.driver_id,
            label: (selectedWorker as Driver)?.first_name
              ? `${(selectedWorker as Driver)?.first_name} ${
                  (selectedWorker as Driver)?.last_name
                }`
              : '',
          }
        : undefined,
      vehicle: team
        ? {
            value: Number(team?.id),
            label: team?.name || team?.model,
          }
        : undefined,
    });
  }, [isOpen, reset, team, route, route.driver, route.team, selectedWorker]);

  return (
    <Modal isOpen={isOpen} showCloseButton={true} onClose={onClose}>
      <div className={styles.header}>
        <div className={styles.header__title}>
          <Typography variant="h2">Send Job</Typography>
          <Typography variant="p">
            Please confirm the {isIndividual ? 'worker' : 'team'} assigned
          </Typography>
        </div>
      </div>
      <form className={styles.form} onSubmit={handleSubmit(handleSubmitForm)}>
        <FormGroup>
          <Controller
            name="route_name"
            control={control}
            render={({field, fieldState}) => (
              <TextField
                label="Route name"
                placeholder="Enter route name"
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
        </FormGroup>
        {isIndividual ? (
          <FormGroup>
            <Controller
              name="driver"
              control={control}
              rules={{required: true}}
              render={({field, fieldState}) => (
                <SelectField
                  // isDisabled
                  label="Assigned worker"
                  placeholder="Click To Select"
                  isLoading={false}
                  options={workers.map((worker) => ({
                    value: worker.driver_id || 0,
                    label: worker.first_name + ' ' + worker.last_name,
                  }))}
                  error={fieldState.error?.message}
                  {...field}
                  onChange={(value) => {
                    onChangeWorker(
                      workers.find(
                        (worker) => worker.driver_id === Number(value?.value)
                      ) as Driver
                    );
                    field.onChange(value);
                  }}
                />
              )}
            />
          </FormGroup>
        ) : (
          <FormGroup>
            <Controller
              name="vehicle"
              control={control}
              rules={{required: true}}
              render={({field, fieldState}) => (
                <SelectField
                  label="Assigned team"
                  placeholder="Click To Select"
                  isLoading={false}
                  options={teams.map((team) => ({
                    value: team.id || 0,
                    label: team.name,
                  }))}
                  error={fieldState.error?.message}
                  {...field}
                  onChange={(value) => {
                    onChangeWorker(
                      teams.find(
                        (team) => team.id === Number(value?.value)
                      ) as Vehicle
                    );
                    field.onChange(value);
                  }}
                />
              )}
            />
          </FormGroup>
        )}

        <div className={styles.actions}>
          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Confirm
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SendRouteModal;
